import { makeObservable, observable } from 'mobx';

import { APP_WIDGET_LOGIN_MENU_ID } from '../constants';
import type {
  CacheService,
  I$WWrapper,
  MenuItem,
  MenuServiceBase as IMenuServiceBase,
  RouteConfiguration,
  StateService as IStateService,
} from '../types';
import { normalizeMenuItemsLinkMetadata } from '../utils';
import { getLoginBarMenu } from '../utils/login-bar-menu';

const getInitialMenuItems = ($w: I$WWrapper) => getLoginBarMenu($w).menuItems;

const findRouteByMenuItem = (routes: RouteConfiguration[], item: MenuItem) =>
  routes.find((route) => item.link?.includes(route.path));

const getIsTabAccessibleWithUserRoles = (
  currentUserRoles: string[],
  visibleForRoles: string[],
) => {
  if (!visibleForRoles.length) {
    return true;
  }

  return currentUserRoles.some((role) => visibleForRoles.includes(role));
};

export default class MenuServiceBase<T extends IStateService = IStateService>
  implements IMenuServiceBase
{
  menuItems: MenuItem[];

  constructor(
    private readonly $w: I$WWrapper,
    protected stateService: T,
    protected cacheService: CacheService,
  ) {
    this.menuItems = getInitialMenuItems(this.$w);
    makeObservable<this, keyof IMenuServiceBase>(this, {
      menuItems: observable,
    });
  }

  protected setNotificationsCount() {
    const notificationsCounter =
      this.stateService.notificationsCounter?.counters?.find(
        (counter) => counter.key === 'notificationsCount',
      );
    const displayCount = notificationsCounter?.count;
    const appWidgetsLoginMenus: MenuItem[] = this.$w(APP_WIDGET_LOGIN_MENU_ID);
    appWidgetsLoginMenus.forEach((widget) => {
      if (widget.navBarItems?.length) {
        widget.navBarItems = normalizeMenuItemsLinkMetadata([
          { ...widget.navBarItems[0], displayCount },
        ]);
      }
    });
  }

  protected bindMenuItems() {
    // TODO: https://wix.atlassian.net/browse/MA-4435
    const menuItemsToSet = normalizeMenuItemsLinkMetadata(this.menuItems);

    this.$w(APP_WIDGET_LOGIN_MENU_ID).menuItems = menuItemsToSet;
  }

  protected filterMenuItemsVisibleForRoles() {
    const { routes, currentUserRoles } = this.stateService;

    this.menuItems = this.menuItems.filter((item) => {
      const routeConfig = findRouteByMenuItem(routes, item);
      if (!routeConfig?.vfr?.length) {
        return true;
      }
      return getIsTabAccessibleWithUserRoles(currentUserRoles, routeConfig.vfr);
    });
  }
}
